
import { useState } from 'react';

const TermsPage = () => {
  const [activeSection, setActiveSection] = useState<string | null>(null);

  const sections = [
    {
      id: 'definitions',
      title: '1. Definitions',
      icon: 'ri-book-line',
      color: 'text-blue-600',
      content: (
        <div className="space-y-4">
          <p className="text-gray-700 leading-relaxed">
            In these Terms and Conditions, unless the context otherwise requires, the following terms shall have the meanings set forth below:
          </p>
          <div className="space-y-3">
            <div className="border-l-4 border-blue-500 pl-4">
              <p className="font-semibold text-gray-900">"Company", "We", "Us", "Our"</p>
              <p className="text-gray-700">Refers to GlobalShip Express, registered at 123 Logistics Avenue, Singapore 018956.</p>
            </div>
            <div className="border-l-4 border-blue-500 pl-4">
              <p className="font-semibold text-gray-900">"Customer", "You", "Your"</p>
              <p className="text-gray-700">Refers to any individual or entity using our shipping and logistics services.</p>
            </div>
            <div className="border-l-4 border-blue-500 pl-4">
              <p className="font-semibold text-gray-900">"Services"</p>
              <p className="text-gray-700">Refers to all shipping, logistics, freight forwarding, customs clearance, warehousing, and related services provided by the Company.</p>
            </div>
            <div className="border-l-4 border-blue-500 pl-4">
              <p className="font-semibold text-gray-900">"Shipment" or "Goods"</p>
              <p className="text-gray-700">Refers to any package, parcel, cargo, or freight entrusted to the Company for transportation.</p>
            </div>
            <div className="border-l-4 border-blue-500 pl-4">
              <p className="font-semibold text-gray-900">"Consignor"</p>
              <p className="text-gray-700">The party who delivers the goods to the Company for shipment.</p>
            </div>
            <div className="border-l-4 border-blue-500 pl-4">
              <p className="font-semibold text-gray-900">"Consignee"</p>
              <p className="text-gray-700">The party to whom the goods are to be delivered.</p>
            </div>
            <div className="border-l-4 border-blue-500 pl-4">
              <p className="font-semibold text-gray-900">"Dangerous Goods"</p>
              <p className="text-gray-700">Any goods classified as hazardous, dangerous, or prohibited under international regulations including IATA, IMDG, or local laws.</p>
            </div>
          </div>
        </div>
      )
    },
    {
      id: 'service-description',
      title: '2. Service Description',
      icon: 'ri-truck-line',
      color: 'text-green-600',
      content: (
        <div className="space-y-4">
          <p className="text-gray-700 leading-relaxed">
            GlobalShip Express provides comprehensive international and domestic shipping and logistics services including but not limited to:
          </p>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="bg-blue-50 p-4 rounded-lg">
              <h4 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                <i className="ri-ship-line text-blue-600"></i>
                Sea Freight Services
              </h4>
              <ul className="text-sm text-gray-700 space-y-1 ml-6 list-disc">
                <li>Full Container Load (FCL)</li>
                <li>Less than Container Load (LCL)</li>
                <li>Roll-on/Roll-off (RoRo)</li>
                <li>Break Bulk Cargo</li>
              </ul>
            </div>
            <div className="bg-green-50 p-4 rounded-lg">
              <h4 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                <i className="ri-plane-line text-green-600"></i>
                Air Freight Services
              </h4>
              <ul className="text-sm text-gray-700 space-y-1 ml-6 list-disc">
                <li>Express Air Freight</li>
                <li>Standard Air Cargo</li>
                <li>Charter Services</li>
                <li>Temperature-Controlled Transport</li>
              </ul>
            </div>
            <div className="bg-blue-50 p-4 rounded-lg">
              <h4 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                <i className="ri-roadster-line text-blue-600"></i>
                Land Transport
              </h4>
              <ul className="text-sm text-gray-700 space-y-1 ml-6 list-disc">
                <li>Road Freight</li>
                <li>Rail Transport</li>
                <li>Last-Mile Delivery</li>
                <li>Cross-Border Transport</li>
              </ul>
            </div>
            <div className="bg-green-50 p-4 rounded-lg">
              <h4 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                <i className="ri-file-list-line text-green-600"></i>
                Additional Services
              </h4>
              <ul className="text-sm text-gray-700 space-y-1 ml-6 list-disc">
                <li>Customs Clearance</li>
                <li>Warehousing & Storage</li>
                <li>Cargo Insurance</li>
                <li>Packaging & Crating</li>
              </ul>
            </div>
          </div>
          <p className="text-gray-700 leading-relaxed mt-4">
            All services are subject to availability, capacity, and compliance with applicable laws and regulations. The Company reserves the right to refuse service for any shipment that does not meet our requirements or poses safety, legal, or operational concerns.
          </p>
        </div>
      )
    },
    {
      id: 'user-obligations',
      title: '3. User Obligations',
      icon: 'ri-user-settings-line',
      color: 'text-blue-600',
      content: (
        <div className="space-y-4">
          <p className="text-gray-700 leading-relaxed">
            By using our services, you agree to the following obligations:
          </p>
          <div className="space-y-4">
            <div className="bg-gray-50 p-4 rounded-lg">
              <h4 className="font-semibold text-gray-900 mb-2">3.1 Accurate Information</h4>
              <p className="text-gray-700 text-sm leading-relaxed">
                You must provide complete, accurate, and truthful information regarding the shipment, including but not limited to: sender and recipient details, contents description, value declaration, weight and dimensions, and any special handling requirements. False or misleading information may result in delays, additional charges, confiscation, or legal consequences.
              </p>
            </div>
            <div className="bg-gray-50 p-4 rounded-lg">
              <h4 className="font-semibold text-gray-900 mb-2">3.2 Proper Packaging</h4>
              <p className="text-gray-700 text-sm leading-relaxed">
                You are responsible for ensuring that all goods are properly and securely packaged to withstand normal handling and transportation conditions. The Company is not liable for damage resulting from inadequate packaging. Fragile items must be clearly marked and declared.
              </p>
            </div>
            <div className="bg-gray-50 p-4 rounded-lg">
              <h4 className="font-semibold text-gray-900 mb-2">3.3 Prohibited Items</h4>
              <p className="text-gray-700 text-sm leading-relaxed">
                You must not ship any prohibited or restricted items as outlined in Section 9 of these Terms. You are responsible for understanding and complying with all applicable import/export regulations, customs requirements, and international shipping restrictions.
              </p>
            </div>
            <div className="bg-gray-50 p-4 rounded-lg">
              <h4 className="font-semibold text-gray-900 mb-2">3.4 Documentation</h4>
              <p className="text-gray-700 text-sm leading-relaxed">
                You must provide all necessary documentation including commercial invoices, packing lists, certificates of origin, export licenses, and any other documents required for customs clearance and international shipping. Incomplete documentation may result in delays and additional charges.
              </p>
            </div>
            <div className="bg-gray-50 p-4 rounded-lg">
              <h4 className="font-semibold text-gray-900 mb-2">3.5 Payment Obligations</h4>
              <p className="text-gray-700 text-sm leading-relaxed">
                You agree to pay all charges associated with the shipment including freight charges, fuel surcharges, customs duties, taxes, storage fees, and any additional charges incurred. Payment terms are as agreed upon booking or as specified in your account agreement.
              </p>
            </div>
            <div className="bg-gray-50 p-4 rounded-lg">
              <h4 className="font-semibold text-gray-900 mb-2">3.6 Compliance with Laws</h4>
              <p className="text-gray-700 text-sm leading-relaxed">
                You must comply with all applicable local, national, and international laws and regulations governing the shipment of goods, including but not limited to export controls, sanctions, customs regulations, and trade compliance requirements.
              </p>
            </div>
          </div>
        </div>
      )
    },
    {
      id: 'shipping-terms',
      title: '4. Shipping Terms',
      icon: 'ri-global-line',
      color: 'text-green-600',
      content: (
        <div className="space-y-4">
          <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded">
            <h4 className="font-semibold text-gray-900 mb-2">4.1 Transit Times</h4>
            <p className="text-gray-700 text-sm leading-relaxed">
              Transit times are estimates only and are not guaranteed unless specifically agreed in writing as part of a premium service. Transit times may vary due to weather conditions, customs delays, operational requirements, force majeure events, or other circumstances beyond our control. The Company is not liable for delays unless caused by gross negligence.
            </p>
          </div>
          <div className="bg-green-50 border-l-4 border-green-500 p-4 rounded">
            <h4 className="font-semibold text-gray-900 mb-2">4.2 Delivery Attempts</h4>
            <p className="text-gray-700 text-sm leading-relaxed">
              For door-to-door services, we will make up to three (3) delivery attempts at the specified address. If delivery cannot be completed after three attempts, the shipment will be held at our facility for collection or returned to sender at your expense. Additional storage charges may apply after 5 business days.
            </p>
          </div>
          <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded">
            <h4 className="font-semibold text-gray-900 mb-2">4.3 Customs Clearance</h4>
            <p className="text-gray-700 text-sm leading-relaxed">
              All international shipments are subject to customs clearance in the destination country. Customs clearance times vary and are beyond our control. You are responsible for providing accurate customs documentation and paying all applicable duties, taxes, and customs fees. The Company may advance these charges on your behalf and invoice you accordingly.
            </p>
          </div>
          <div className="bg-green-50 border-l-4 border-green-500 p-4 rounded">
            <h4 className="font-semibold text-gray-900 mb-2">4.4 Routing and Method</h4>
            <p className="text-gray-700 text-sm leading-relaxed">
              The Company reserves the right to determine the routing, method of transportation, and carriers used for your shipment. We may consolidate shipments, use subcontractors, or change routing as necessary to ensure efficient and cost-effective delivery while maintaining service quality standards.
            </p>
          </div>
          <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded">
            <h4 className="font-semibold text-gray-900 mb-2">4.5 Inspection Rights</h4>
            <p className="text-gray-700 text-sm leading-relaxed">
              The Company and relevant authorities reserve the right to inspect any shipment at any time for security, safety, customs, or regulatory compliance purposes. You consent to such inspections by using our services. We are not liable for any delays or damage resulting from inspections conducted by authorities.
            </p>
          </div>
          <div className="bg-green-50 border-l-4 border-green-500 p-4 rounded">
            <h4 className="font-semibold text-gray-900 mb-2">4.6 Refused or Undeliverable Shipments</h4>
            <p className="text-gray-700 text-sm leading-relaxed">
              If a shipment is refused by the consignee, cannot be delivered due to incorrect address, or is abandoned, the Company will attempt to contact the consignor. If no instructions are received within 10 business days, the Company may dispose of the goods and charge all associated costs including storage, handling, and disposal fees to the consignor.
            </p>
          </div>
        </div>
      )
    },
    {
      id: 'pricing-payment',
      title: '5. Pricing & Payment',
      icon: 'ri-money-dollar-circle-line',
      color: 'text-blue-600',
      content: (
        <div className="space-y-4">
          <div className="bg-gray-50 p-4 rounded-lg">
            <h4 className="font-semibold text-gray-900 mb-2">5.1 Pricing Structure</h4>
            <p className="text-gray-700 text-sm leading-relaxed mb-3">
              Our pricing is based on various factors including but not limited to: shipment weight and dimensions (whichever is greater using volumetric weight calculation), origin and destination, service type and speed, fuel surcharges, security fees, customs clearance fees, and any special handling requirements.
            </p>
            <div className="bg-white p-3 rounded border border-gray-200">
              <p className="text-sm text-gray-700 mb-2"><strong>Volumetric Weight Formula:</strong></p>
              <p className="text-sm text-gray-600 font-mono">Length (cm) × Width (cm) × Height (cm) ÷ 5000 = Volumetric Weight (kg)</p>
            </div>
          </div>
          <div className="bg-gray-50 p-4 rounded-lg">
            <h4 className="font-semibold text-gray-900 mb-2">5.2 Quotes and Estimates</h4>
            <p className="text-gray-700 text-sm leading-relaxed">
              All quotes and estimates are valid for 30 days from the date of issue unless otherwise specified. Quotes are based on the information provided and are subject to verification. If actual shipment details differ from the quoted information (weight, dimensions, contents, destination), additional charges may apply.
            </p>
          </div>
          <div className="bg-gray-50 p-4 rounded-lg">
            <h4 className="font-semibold text-gray-900 mb-2">5.3 Payment Terms</h4>
            <p className="text-gray-700 text-sm leading-relaxed mb-2">
              Payment terms vary based on account type:
            </p>
            <ul className="text-sm text-gray-700 space-y-1 ml-6 list-disc">
              <li><strong>Cash Customers:</strong> Payment in full required before shipment</li>
              <li><strong>Credit Account Holders:</strong> Net 30 days from invoice date</li>
              <li><strong>Corporate Accounts:</strong> As per signed agreement</li>
            </ul>
            <p className="text-gray-700 text-sm leading-relaxed mt-2">
              Accepted payment methods include credit/debit cards, bank transfers, PayPal, and approved credit accounts. A 1.5% monthly interest charge (18% APR) applies to overdue accounts.
            </p>
          </div>
          <div className="bg-gray-50 p-4 rounded-lg">
            <h4 className="font-semibold text-gray-900 mb-2">5.4 Additional Charges</h4>
            <p className="text-gray-700 text-sm leading-relaxed mb-2">
              You may incur additional charges for:
            </p>
            <ul className="text-sm text-gray-700 space-y-1 ml-6 list-disc">
              <li>Customs duties, taxes, and clearance fees</li>
              <li>Storage fees (after 5 free days): $15/day for standard cargo, $50/day for refrigerated cargo</li>
              <li>Redelivery attempts: $25 per attempt</li>
              <li>Address corrections: $20 per correction</li>
              <li>Special handling (fragile, hazardous, oversized): 15-50% surcharge</li>
              <li>Remote area delivery: $50-150 depending on location</li>
              <li>Fuel surcharges: Variable, updated monthly</li>
              <li>Peak season surcharges: Applied during high-demand periods</li>
            </ul>
          </div>
          <div className="bg-gray-50 p-4 rounded-lg">
            <h4 className="font-semibold text-gray-900 mb-2">5.5 Currency and Exchange Rates</h4>
            <p className="text-gray-700 text-sm leading-relaxed">
              All prices are quoted in USD unless otherwise specified. For payments in other currencies, the exchange rate applied will be the rate on the date of invoice plus a 2% currency conversion fee. We are not responsible for exchange rate fluctuations between quote and payment dates.
            </p>
          </div>
          <div className="bg-gray-50 p-4 rounded-lg">
            <h4 className="font-semibold text-gray-900 mb-2">5.6 Lien Rights</h4>
            <p className="text-gray-700 text-sm leading-relaxed">
              The Company has a general lien on all goods and documents in our possession for all sums due from you, whether related to the specific shipment or not. We may refuse to release goods until all outstanding charges are paid in full. If charges remain unpaid for 30 days, we may sell the goods to recover our costs.
            </p>
          </div>
        </div>
      )
    },
    {
      id: 'liability',
      title: '6. Liability Limitations',
      icon: 'ri-shield-check-line',
      color: 'text-green-600',
      content: (
        <div className="space-y-4">
          <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded">
            <h4 className="font-semibold text-gray-900 mb-2">6.1 Maximum Liability</h4>
            <p className="text-gray-700 text-sm leading-relaxed">
              Unless additional insurance is purchased, the Company's maximum liability for loss or damage to any shipment is limited to the lesser of: (a) the actual value of the goods, (b) $100 per kilogram of gross weight, or (c) $50,000 per shipment. This limitation applies regardless of the cause of loss or damage, including negligence.
            </p>
          </div>
          <div className="bg-yellow-50 border-l-4 border-yellow-500 p-4 rounded">
            <h4 className="font-semibold text-gray-900 mb-2">6.2 Excluded Losses</h4>
            <p className="text-gray-700 text-sm leading-relaxed mb-2">
              The Company is not liable for:
            </p>
            <ul className="text-sm text-gray-700 space-y-1 ml-6 list-disc">
              <li>Indirect, consequential, special, or punitive damages including loss of profits, loss of business, or loss of opportunity</li>
              <li>Damage caused by inherent vice, nature of the goods, or inadequate packaging</li>
              <li>Loss or damage to prohibited items or undeclared goods</li>
              <li>Delays in delivery unless caused by gross negligence and guaranteed delivery was purchased</li>
              <li>Acts of God, force majeure, war, terrorism, civil unrest, or government actions</li>
              <li>Customs seizure, confiscation, or destruction of goods</li>
              <li>Loss or damage to data, software, or electronic files</li>
              <li>Damage to goods with declared value less than actual value</li>
            </ul>
          </div>
          <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded">
            <h4 className="font-semibold text-gray-900 mb-2">6.3 Claims Procedure</h4>
            <p className="text-gray-700 text-sm leading-relaxed">
              All claims for loss or damage must be submitted in writing within 14 days of delivery (or scheduled delivery date for lost shipments). Claims must include: original shipping documents, proof of value, photographs of damage, detailed description of loss/damage, and any other supporting documentation. Claims submitted after 14 days will not be accepted. The Company reserves the right to inspect damaged goods before processing any claim.
            </p>
          </div>
          <div className="bg-green-50 border-l-4 border-green-500 p-4 rounded">
            <h4 className="font-semibold text-gray-900 mb-2">6.4 Third-Party Liability</h4>
            <p className="text-gray-700 text-sm leading-relaxed">
              When the Company uses subcontractors, agents, or third-party carriers, our liability is limited to the liability of such third parties under their terms and conditions. The Company acts as an agent in arranging such services and is not liable for the acts or omissions of third parties.
            </p>
          </div>
          <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded">
            <h4 className="font-semibold text-gray-900 mb-2">6.5 Time Limitation for Legal Action</h4>
            <p className="text-gray-700 text-sm leading-relaxed">
              Any legal action against the Company must be commenced within nine (9) months from the date of delivery or scheduled delivery date. This time limitation applies regardless of the nature of the claim or the legal theory asserted.
            </p>
          </div>
        </div>
      )
    },
    {
      id: 'insurance',
      title: '7. Insurance Coverage',
      icon: 'ri-shield-star-line',
      color: 'text-blue-600',
      content: (
        <div className="space-y-4">
          <div className="bg-blue-50 p-4 rounded-lg">
            <h4 className="font-semibold text-gray-900 mb-2">7.1 Standard Coverage</h4>
            <p className="text-gray-700 text-sm leading-relaxed">
              All shipments include basic liability coverage as outlined in Section 6.1 at no additional charge. This basic coverage is limited and may not cover the full value of your goods. We strongly recommend purchasing additional insurance for valuable or fragile items.
            </p>
          </div>
          <div className="bg-green-50 p-4 rounded-lg">
            <h4 className="font-semibold text-gray-900 mb-2">7.2 Additional Insurance</h4>
            <p className="text-gray-700 text-sm leading-relaxed mb-3">
              Additional cargo insurance is available at the following rates:
            </p>
            <div className="bg-white p-3 rounded border border-gray-200 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-700">Declared Value up to $5,000:</span>
                <span className="font-semibold text-gray-900">2.5% of declared value</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-700">Declared Value $5,001 - $25,000:</span>
                <span className="font-semibold text-gray-900">2.0% of declared value</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-700">Declared Value over $25,000:</span>
                <span className="font-semibold text-gray-900">1.5% of declared value</span>
              </div>
              <div className="flex justify-between text-sm pt-2 border-t border-gray-200">
                <span className="text-gray-700">Minimum Insurance Premium:</span>
                <span className="font-semibold text-gray-900">$25 per shipment</span>
              </div>
            </div>
          </div>
          <div className="bg-blue-50 p-4 rounded-lg">
            <h4 className="font-semibold text-gray-900 mb-2">7.3 Insurance Coverage Details</h4>
            <p className="text-gray-700 text-sm leading-relaxed mb-2">
              Additional insurance covers:
            </p>
            <ul className="text-sm text-gray-700 space-y-1 ml-6 list-disc">
              <li>Physical loss or damage during transit</li>
              <li>Theft or pilferage</li>
              <li>Fire, explosion, or natural disasters</li>
              <li>Accidents during loading/unloading</li>
              <li>Water damage (excluding inherent vice)</li>
            </ul>
            <p className="text-gray-700 text-sm leading-relaxed mt-3">
              Insurance does not cover: wear and tear, gradual deterioration, inherent vice, inadequate packaging, delays, indirect losses, or items not properly declared.
            </p>
          </div>
          <div className="bg-green-50 p-4 rounded-lg">
            <h4 className="font-semibold text-gray-900 mb-2">7.4 Insurance Claims</h4>
            <p className="text-gray-700 text-sm leading-relaxed">
              Insurance claims must be filed within 7 days of delivery or discovery of loss/damage. Claims require: proof of value (invoice, receipt, or appraisal), photographs of damage, original packaging (if damaged), police report (for theft), and completed claim form. The insurance company reserves the right to inspect damaged goods and may require independent assessment.
            </p>
          </div>
          <div className="bg-blue-50 p-4 rounded-lg">
            <h4 className="font-semibold text-gray-900 mb-2">7.5 High-Value Items</h4>
            <p className="text-gray-700 text-sm leading-relaxed">
              For shipments valued over $50,000, special insurance arrangements must be made in advance. Contact our insurance department at insurance@globalshipexpress.com or +1-800-555-0199 at least 48 hours before shipment. Additional documentation and security measures may be required.
            </p>
          </div>
        </div>
      )
    },
    {
      id: 'prohibited-items',
      title: '9. Prohibited Items',
      icon: 'ri-forbid-line',
      color: 'text-red-600',
      content: (
        <div className="space-y-4">
          <div className="bg-red-50 border-2 border-red-500 p-4 rounded-lg">
            <h4 className="font-semibold text-red-900 mb-2 flex items-center gap-2">
              <i className="ri-alert-line"></i>
              Absolutely Prohibited Items
            </h4>
            <p className="text-gray-700 text-sm leading-relaxed mb-2">
              The following items are strictly prohibited and will not be accepted under any circumstances:
            </p>
            <div className="grid md:grid-cols-2 gap-3">
              <ul className="text-sm text-gray-700 space-y-1 ml-6 list-disc">
                <li>Explosives, fireworks, ammunition, and weapons</li>
                <li>Illegal drugs and narcotics</li>
                <li>Counterfeit goods and pirated products</li>
                <li>Human remains and body parts</li>
                <li>Live animals (except as authorized)</li>
                <li>Radioactive materials</li>
                <li>Biological hazards and infectious substances</li>
              </ul>
              <ul className="text-sm text-gray-700 space-y-1 ml-6 list-disc">
                <li>Stolen goods and contraband</li>
                <li>Pornographic materials (in certain jurisdictions)</li>
                <li>Ivory and endangered species products</li>
                <li>Currency and negotiable instruments (over $10,000)</li>
                <li>Hazardous waste and toxic substances</li>
                <li>Items violating international sanctions</li>
                <li>Unmanifested or undeclared goods</li>
              </ul>
            </div>
          </div>
          <div className="bg-yellow-50 border-2 border-yellow-500 p-4 rounded-lg">
            <h4 className="font-semibold text-yellow-900 mb-2 flex items-center gap-2">
              <i className="ri-error-warning-line"></i>
              Restricted Items (Require Special Authorization)
            </h4>
            <p className="text-gray-700 text-sm leading-relaxed mb-2">
              The following items may be shipped with proper documentation, authorization, and compliance with regulations:
            </p>
            <div className="grid md:grid-cols-2 gap-3">
              <ul className="text-sm text-gray-700 space-y-1 ml-6 list-disc">
                <li>Lithium batteries (must comply with IATA regulations)</li>
                <li>Perfumes and aerosols (limited quantities)</li>
                <li>Alcoholic beverages (with proper licenses)</li>
                <li>Tobacco products (with tax documentation)</li>
                <li>Pharmaceuticals and medical devices</li>
                <li>Perishable foods (temperature-controlled)</li>
              </ul>
              <ul className="text-sm text-gray-700 space-y-1 ml-6 list-disc">
                <li>Precious metals and gemstones</li>
                <li>Artwork and antiques (with certificates)</li>
                <li>Chemicals and laboratory samples</li>
                <li>Dry ice (limited quantities)</li>
                <li>Magnetic materials</li>
                <li>Plants and seeds (with phytosanitary certificates)</li>
              </ul>
            </div>
          </div>
          <div className="bg-gray-50 p-4 rounded-lg">
            <h4 className="font-semibold text-gray-900 mb-2">Consequences of Shipping Prohibited Items</h4>
            <p className="text-gray-700 text-sm leading-relaxed mb-2">
              If prohibited or undeclared restricted items are discovered in your shipment:
            </p>
            <ul className="text-sm text-gray-700 space-y-1 ml-6 list-disc">
              <li>The shipment will be immediately stopped and may be confiscated by authorities</li>
              <li>You will be liable for all fines, penalties, and legal costs incurred</li>
              <li>Your account may be suspended or terminated</li>
              <li>You may face criminal prosecution under applicable laws</li>
              <li>The Company will cooperate fully with law enforcement agencies</li>
              <li>No refund will be provided for shipping charges</li>
              <li>You will be responsible for disposal costs if applicable</li>
            </ul>
          </div>
          <div className="bg-blue-50 p-4 rounded-lg">
            <h4 className="font-semibold text-gray-900 mb-2">Country-Specific Restrictions</h4>
            <p className="text-gray-700 text-sm leading-relaxed">
              Import and export restrictions vary by country. You are responsible for verifying what items are permitted in the destination country. Common country-specific restrictions include: religious materials, political materials, certain foods and agricultural products, telecommunications equipment, and encryption devices. Contact our compliance team for guidance on specific destinations.
            </p>
          </div>
        </div>
      )
    },
    {
      id: 'force-majeure',
      title: '10. Force Majeure',
      icon: 'ri-thunderstorms-line',
      color: 'text-green-600',
      content: (
        <div className="space-y-4">
          <p className="text-gray-700 leading-relaxed">
            The Company shall not be liable for any failure or delay in performing its obligations under these Terms if such failure or delay is due to circumstances beyond our reasonable control.
          </p>
          <div className="bg-gray-50 p-4 rounded-lg">
            <h4 className="font-semibold text-gray-900 mb-2">Force Majeure Events Include:</h4>
            <div className="grid md:grid-cols-2 gap-3">
              <ul className="text-sm text-gray-700 space-y-1 ml-6 list-disc">
                <li>Natural disasters (earthquakes, floods, hurricanes, tsunamis)</li>
                <li>Severe weather conditions affecting transportation</li>
                <li>War, armed conflict, or acts of terrorism</li>
                <li>Civil unrest, riots, or political instability</li>
                <li>Pandemics, epidemics, or public health emergencies</li>
                <li>Government actions, embargoes, or sanctions</li>
              </ul>
              <ul className="text-sm text-gray-700 space-y-1 ml-6 list-disc">
                <li>Strikes, labor disputes, or lockouts</li>
                <li>Port closures or transportation disruptions</li>
                <li>Cyber attacks or system failures</li>
                <li>Fire, explosion, or industrial accidents</li>
                <li>Shortage of fuel, energy, or transportation capacity</li>
                <li>Any other event beyond reasonable control</li>
              </ul>
            </div>
          </div>
          <div className="bg-blue-50 p-4 rounded-lg">
            <h4 className="font-semibold text-gray-900 mb-2">Effect of Force Majeure</h4>
            <p className="text-gray-700 text-sm leading-relaxed">
              During a force majeure event, the Company's obligations will be suspended for the duration of the event. We will make reasonable efforts to minimize delays and notify affected customers. If the force majeure event continues for more than 30 days, either party may terminate the affected shipment contract without liability. No refunds will be provided for services already rendered.
            </p>
          </div>
          <div className="bg-green-50 p-4 rounded-lg">
            <h4 className="font-semibold text-gray-900 mb-2">Notification</h4>
            <p className="text-gray-700 text-sm leading-relaxed">
              The Company will use reasonable efforts to notify customers of force majeure events affecting their shipments through email, website updates, or other available communication channels. However, we are not liable for failure to provide notice during emergency situations.
            </p>
          </div>
        </div>
      )
    },
    {
      id: 'termination',
      title: '11. Termination',
      icon: 'ri-close-circle-line',
      color: 'text-blue-600',
      content: (
        <div className="space-y-4">
          <div className="bg-gray-50 p-4 rounded-lg">
            <h4 className="font-semibold text-gray-900 mb-2">11.1 Termination by Customer</h4>
            <p className="text-gray-700 text-sm leading-relaxed">
              You may terminate your account at any time by providing 30 days written notice to accounts@globalshipexpress.com. You remain responsible for all charges incurred before termination and any shipments in transit. Outstanding balances must be paid in full before account closure.
            </p>
          </div>
          <div className="bg-gray-50 p-4 rounded-lg">
            <h4 className="font-semibold text-gray-900 mb-2">11.2 Termination by Company</h4>
            <p className="text-gray-700 text-sm leading-relaxed mb-2">
              The Company may suspend or terminate your account immediately without notice if:
            </p>
            <ul className="text-sm text-gray-700 space-y-1 ml-6 list-disc">
              <li>You breach any provision of these Terms and Conditions</li>
              <li>You ship prohibited items or provide false information</li>
              <li>Your account has overdue payments exceeding 60 days</li>
              <li>You engage in fraudulent or illegal activities</li>
              <li>You abuse or threaten our staff or agents</li>
              <li>Your shipments repeatedly cause operational issues</li>
              <li>We are required to do so by law or regulatory authority</li>
            </ul>
          </div>
          <div className="bg-gray-50 p-4 rounded-lg">
            <h4 className="font-semibold text-gray-900 mb-2">11.3 Effect of Termination</h4>
            <p className="text-gray-700 text-sm leading-relaxed">
              Upon termination: (a) your right to use our services ceases immediately, (b) all outstanding charges become immediately due and payable, (c) shipments in transit will be completed or returned at your expense, (d) stored goods must be collected within 10 days or will be disposed of at your cost, (e) any prepaid credits are forfeited, and (f) provisions regarding liability, indemnification, and dispute resolution survive termination.
            </p>
          </div>
        </div>
      )
    },
    {
      id: 'governing-law',
      title: '12. Governing Law & Jurisdiction',
      icon: 'ri-scales-line',
      color: 'text-green-600',
      content: (
        <div className="space-y-4">
          <div className="bg-blue-50 p-4 rounded-lg">
            <h4 className="font-semibold text-gray-900 mb-2">12.1 Applicable Law</h4>
            <p className="text-gray-700 text-sm leading-relaxed">
              These Terms and Conditions shall be governed by and construed in accordance with the laws of Singapore, without regard to its conflict of law provisions. International shipments are also subject to applicable international conventions including the Warsaw Convention (air transport), the Hague-Visby Rules (sea transport), and the CMR Convention (road transport) where applicable.
            </p>
          </div>
          <div className="bg-green-50 p-4 rounded-lg">
            <h4 className="font-semibold text-gray-900 mb-2">12.2 Jurisdiction</h4>
            <p className="text-gray-700 text-sm leading-relaxed">
              Any disputes arising from or relating to these Terms or our services shall be subject to the exclusive jurisdiction of the courts of Singapore. However, the Company may bring proceedings in any other jurisdiction where you reside or where your assets are located for the purpose of enforcing these Terms or collecting outstanding debts.
            </p>
          </div>
          <div className="bg-blue-50 p-4 rounded-lg">
            <h4 className="font-semibold text-gray-900 mb-2">12.3 Language</h4>
            <p className="text-gray-700 text-sm leading-relaxed">
              These Terms and Conditions are drafted in English. Any translations provided are for convenience only. In case of any discrepancy between the English version and any translation, the English version shall prevail.
            </p>
          </div>
        </div>
      )
    },
    {
      id: 'dispute-resolution',
      title: '13. Dispute Resolution',
      icon: 'ri-discuss-line',
      color: 'text-blue-600',
      content: (
        <div className="space-y-4">
          <div className="bg-gray-50 p-4 rounded-lg">
            <h4 className="font-semibold text-gray-900 mb-2">13.1 Negotiation</h4>
            <p className="text-gray-700 text-sm leading-relaxed">
              In the event of any dispute, controversy, or claim arising out of or relating to these Terms or our services, the parties agree to first attempt to resolve the matter through good faith negotiations. Either party may initiate negotiations by sending written notice to the other party describing the dispute in detail.
            </p>
          </div>
          <div className="bg-gray-50 p-4 rounded-lg">
            <h4 className="font-semibold text-gray-900 mb-2">13.2 Mediation</h4>
            <p className="text-gray-700 text-sm leading-relaxed">
              If the dispute cannot be resolved through negotiation within 30 days, the parties agree to submit the dispute to mediation administered by the Singapore International Mediation Centre (SIMC) before resorting to arbitration or litigation. The costs of mediation shall be shared equally between the parties.
            </p>
          </div>
          <div className="bg-gray-50 p-4 rounded-lg">
            <h4 className="font-semibold text-gray-900 mb-2">13.3 Arbitration</h4>
            <p className="text-gray-700 text-sm leading-relaxed">
              For disputes exceeding $50,000 USD, if mediation is unsuccessful, the dispute shall be referred to and finally resolved by arbitration administered by the Singapore International Arbitration Centre (SIAC) in accordance with the Arbitration Rules of SIAC. The arbitration shall be conducted in English by a single arbitrator appointed in accordance with SIAC rules. The arbitrator's decision shall be final and binding.
            </p>
          </div>
          <div className="bg-gray-50 p-4 rounded-lg">
            <h4 className="font-semibold text-gray-900 mb-2">13.4 Small Claims</h4>
            <p className="text-gray-700 text-sm leading-relaxed">
              For disputes not exceeding $50,000 USD, either party may elect to resolve the dispute in small claims court or through the dispute resolution process outlined above. This election must be made in writing within 30 days of the dispute arising.
            </p>
          </div>
          <div className="bg-blue-50 p-4 rounded-lg">
            <h4 className="font-semibold text-gray-900 mb-2">13.5 Class Action Waiver</h4>
            <p className="text-gray-700 text-sm leading-relaxed">
              You agree that any dispute resolution proceedings will be conducted only on an individual basis and not in a class, consolidated, or representative action. You waive any right to participate in a class action lawsuit or class-wide arbitration against the Company.
            </p>
          </div>
        </div>
      )
    },
    {
      id: 'amendments',
      title: '14. Amendments & Updates',
      icon: 'ri-file-edit-line',
      color: 'text-green-600',
      content: (
        <div className="space-y-4">
          <div className="bg-blue-50 p-4 rounded-lg">
            <h4 className="font-semibold text-gray-900 mb-2">14.1 Right to Modify</h4>
            <p className="text-gray-700 text-sm leading-relaxed">
              The Company reserves the right to modify, amend, or update these Terms and Conditions at any time without prior notice. Changes may be necessary due to legal requirements, operational changes, new services, or other business reasons. It is your responsibility to review these Terms periodically for updates.
            </p>
          </div>
          <div className="bg-green-50 p-4 rounded-lg">
            <h4 className="font-semibold text-gray-900 mb-2">14.2 Notification of Changes</h4>
            <p className="text-gray-700 text-sm leading-relaxed">
              For material changes to these Terms, we will make reasonable efforts to notify registered customers via email or through a prominent notice on our website at least 30 days before the changes take effect. However, we may implement changes immediately for legal compliance, security reasons, or to prevent fraud or abuse.
            </p>
          </div>
          <div className="bg-blue-50 p-4 rounded-lg">
            <h4 className="font-semibold text-gray-900 mb-2">14.3 Acceptance of Changes</h4>
            <p className="text-gray-700 text-sm leading-relaxed">
              Your continued use of our services after changes to these Terms constitutes your acceptance of the modified Terms. If you do not agree with the changes, you must stop using our services and close your account. Shipments booked before the effective date of changes will be governed by the Terms in effect at the time of booking.
            </p>
          </div>
          <div className="bg-green-50 p-4 rounded-lg">
            <h4 className="font-semibold text-gray-900 mb-2">14.4 Version History</h4>
            <p className="text-gray-700 text-sm leading-relaxed">
              Previous versions of these Terms and Conditions are archived and available upon request. Contact legal@globalshipexpress.com to request historical versions. The current version is always available on our website with the "Last Updated" date clearly displayed.
            </p>
          </div>
        </div>
      )
    },
    {
      id: 'general',
      title: '15. General Provisions',
      icon: 'ri-file-list-3-line',
      color: 'text-blue-600',
      content: (
        <div className="space-y-4">
          <div className="bg-gray-50 p-4 rounded-lg">
            <h4 className="font-semibold text-gray-900 mb-2">15.1 Entire Agreement</h4>
            <p className="text-gray-700 text-sm leading-relaxed">
              These Terms and Conditions, together with any service-specific terms, quotations, and invoices, constitute the entire agreement between you and the Company regarding the use of our services and supersede all prior agreements, understandings, and communications, whether written or oral.
            </p>
          </div>
          <div className="bg-gray-50 p-4 rounded-lg">
            <h4 className="font-semibold text-gray-900 mb-2">15.2 Severability</h4>
            <p className="text-gray-700 text-sm leading-relaxed">
              If any provision of these Terms is found to be invalid, illegal, or unenforceable by a court of competent jurisdiction, such provision shall be modified to the minimum extent necessary to make it valid and enforceable, or if such modification is not possible, the provision shall be severed from these Terms. The remaining provisions shall continue in full force and effect.
            </p>
          </div>
          <div className="bg-gray-50 p-4 rounded-lg">
            <h4 className="font-semibold text-gray-900 mb-2">15.3 Waiver</h4>
            <p className="text-gray-700 text-sm leading-relaxed">
              No waiver by the Company of any breach or default of these Terms shall be deemed a waiver of any subsequent breach or default. The failure of the Company to enforce any right or provision of these Terms shall not constitute a waiver of such right or provision unless acknowledged and agreed to in writing by the Company.
            </p>
          </div>
          <div className="bg-gray-50 p-4 rounded-lg">
            <h4 className="font-semibold text-gray-900 mb-2">15.4 Assignment</h4>
            <p className="text-gray-700 text-sm leading-relaxed">
              You may not assign, transfer, or delegate your rights or obligations under these Terms without the prior written consent of the Company. The Company may assign these Terms or any rights hereunder to any affiliate, subsidiary, or successor entity, or in connection with any merger, acquisition, or sale of assets.
            </p>
          </div>
          <div className="bg-gray-50 p-4 rounded-lg">
            <h4 className="font-semibold text-gray-900 mb-2">15.5 Third-Party Rights</h4>
            <p className="text-gray-700 text-sm leading-relaxed">
              These Terms are for the benefit of the Company and you only. No third party shall have any right to enforce any provision of these Terms. Subcontractors and agents of the Company are entitled to the benefit of all limitations of liability and defenses available to the Company under these Terms.
            </p>
          </div>
          <div className="bg-gray-50 p-4 rounded-lg">
            <h4 className="font-semibold text-gray-900 mb-2">15.6 Notices</h4>
            <p className="text-gray-700 text-sm leading-relaxed">
              All notices required under these Terms must be in writing and sent to: GlobalShip Express, Legal Department, 123 Logistics Avenue, Singapore 018956, or legal@globalshipexpress.com. Notices to you will be sent to the email address or physical address provided in your account. You must keep your contact information current.
            </p>
          </div>
          <div className="bg-gray-50 p-4 rounded-lg">
            <h4 className="font-semibold text-gray-900 mb-2">15.7 Survival</h4>
            <p className="text-gray-700 text-sm leading-relaxed">
              Provisions of these Terms that by their nature should survive termination shall survive, including but not limited to: liability limitations, indemnification obligations, payment obligations, dispute resolution procedures, and governing law provisions.
            </p>
          </div>
        </div>
      )
    },
    {
      id: 'contact',
      title: '16. Contact Information',
      icon: 'ri-customer-service-line',
      color: 'text-green-600',
      content: (
        <div className="space-y-4">
          <p className="text-gray-700 leading-relaxed">
            For questions, concerns, or inquiries regarding these Terms and Conditions, please contact us:
          </p>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="bg-blue-50 p-4 rounded-lg">
              <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <i className="ri-building-line text-blue-600"></i>
                Company Information
              </h4>
              <div className="space-y-2 text-sm text-gray-700">
                <p><strong>Company Name:</strong> GlobalShip Express Pte Ltd</p>
                <p><strong>Registration No:</strong> 202301234A</p>
                <p><strong>Address:</strong> 123 Logistics Avenue<br />Singapore 018956</p>
              </div>
            </div>
            <div className="bg-green-50 p-4 rounded-lg">
              <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <i className="ri-mail-line text-green-600"></i>
                Contact Details
              </h4>
              <div className="space-y-2 text-sm text-gray-700">
                <p><strong>General Inquiries:</strong><br />info@globalshipexpress.com</p>
                <p><strong>Legal Department:</strong><br />legal@globalshipexpress.com</p>
                <p><strong>Phone:</strong> +65 6123 4567</p>
              </div>
            </div>
            <div className="bg-blue-50 p-4 rounded-lg">
              <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <i className="ri-time-line text-blue-600"></i>
                Business Hours
              </h4>
              <div className="space-y-1 text-sm text-gray-700">
                <p><strong>Monday - Friday:</strong> 8:00 AM - 6:00 PM SGT</p>
                <p><strong>Saturday:</strong> 9:00 AM - 1:00 PM SGT</p>
                <p><strong>Sunday & Public Holidays:</strong> Closed</p>
                <p className="mt-2 text-xs text-gray-600">24/7 emergency support available for existing shipments</p>
              </div>
            </div>
            <div className="bg-green-50 p-4 rounded-lg">
              <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <i className="ri-customer-service-2-line text-green-600"></i>
                Customer Support
              </h4>
              <div className="space-y-2 text-sm text-gray-700">
                <p><strong>Support Email:</strong><br />support@globalshipexpress.com</p>
                <p><strong>Support Hotline:</strong><br />+1-800-555-0123 (Toll-free)</p>
                <p><strong>Response Time:</strong> Within 24 hours</p>
              </div>
            </div>
          </div>
          <div className="bg-gray-50 p-4 rounded-lg border-l-4 border-blue-500">
            <p className="text-sm text-gray-700 leading-relaxed">
              <strong>Important:</strong> For urgent matters related to shipments in transit, customs issues, or delivery problems, please contact our 24/7 emergency hotline at +65 6123 4567 (Option 9) or email emergency@globalshipexpress.com with your tracking number.
            </p>
          </div>
        </div>
      )
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <div className="bg-gradient-to-br from-blue-600 to-blue-800 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-white/10 rounded-full mb-6">
              <i className="ri-file-text-line text-4xl"></i>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-4">Terms & Conditions</h1>
            <p className="text-xl text-blue-100 mb-6">
              Please read these terms carefully before using our services
            </p>
            <div className="inline-flex items-center gap-2 bg-white/10 px-4 py-2 rounded-full text-sm">
              <i className="ri-calendar-line"></i>
              <span>Last Updated: January 15, 2025</span>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {/* Introduction */}
        <div className="bg-blue-50 border-l-4 border-blue-500 p-6 rounded-lg mb-12">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Introduction</h2>
          <p className="text-gray-700 leading-relaxed mb-4">
            Welcome to GlobalShip Express. These Terms and Conditions ("Terms") govern your use of our shipping and logistics services. By using our services, creating an account, or placing a shipment order, you agree to be bound by these Terms in their entirety.
          </p>
          <p className="text-gray-700 leading-relaxed mb-4">
            These Terms constitute a legally binding agreement between you (the "Customer") and GlobalShip Express Pte Ltd (the "Company"). If you do not agree with any part of these Terms, you must not use our services.
          </p>
          <p className="text-gray-700 leading-relaxed">
            We recommend that you print or save a copy of these Terms for your records. These Terms were last updated on January 15, 2025, and may be updated from time to time as described in Section 14.
          </p>
        </div>

        {/* Table of Contents */}
        <div className="bg-gray-50 p-6 rounded-lg mb-12">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Table of Contents</h2>
          <div className="grid md:grid-cols-2 gap-3">
            {sections.map((section) => (
              <a
                key={section.id}
                href={`#${section.id}`}
                onClick={(e) => {
                  e.preventDefault();
                  document.getElementById(section.id)?.scrollIntoView({ behavior: 'smooth' });
                }}
                className="flex items-center gap-3 p-3 bg-white rounded-lg hover:bg-blue-50 transition-colors cursor-pointer group"
              >
                <i className={`${section.icon} ${section.color} text-xl`}></i>
                <span className="text-gray-700 group-hover:text-blue-600 text-sm">{section.title}</span>
              </a>
            ))}
          </div>
        </div>

        {/* Sections */}
        <div className="space-y-8">
          {sections.map((section) => (
            <div
              key={section.id}
              id={section.id}
              className="bg-white border border-gray-200 rounded-lg overflow-hidden"
            >
              <button
                onClick={() => setActiveSection(activeSection === section.id ? null : section.id)}
                className="w-full flex items-center justify-between p-6 hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-center gap-4">
                  <div className={`w-12 h-12 flex items-center justify-center bg-gray-50 rounded-lg ${section.color}`}>
                    <i className={`${section.icon} text-2xl`}></i>
                  </div>
                  <h2 className="text-xl font-bold text-gray-900 text-left">{section.title}</h2>
                </div>
                <i className={`ri-arrow-${activeSection === section.id ? 'up' : 'down'}-s-line text-2xl text-gray-400`}></i>
              </button>
              {activeSection === section.id && (
                <div className="p-6 pt-0 border-t border-gray-100">
                  {section.content}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Acceptance Notice */}
        <div className="mt-12 bg-gradient-to-r from-blue-50 to-green-50 border-2 border-blue-200 p-6 rounded-lg">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 flex items-center justify-center bg-blue-600 text-white rounded-full flex-shrink-0">
              <i className="ri-checkbox-circle-line text-2xl"></i>
            </div>
            <div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Acceptance of Terms</h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                By clicking "I Agree", creating an account, placing an order, or using any of our services, you acknowledge that you have read, understood, and agree to be bound by these Terms and Conditions, as well as our Privacy Policy and any other applicable policies.
              </p>
              <p className="text-sm text-gray-600">
                If you have any questions about these Terms, please contact our legal department at legal@globalshipexpress.com before using our services.
              </p>
            </div>
          </div>
        </div>

        {/* Download Options */}
        <div className="mt-8 flex flex-wrap gap-4 justify-center">
          <button className="flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors whitespace-nowrap">
            <i className="ri-download-line"></i>
            <span>Download PDF</span>
          </button>
          <button className="flex items-center gap-2 px-6 py-3 bg-white text-gray-700 border-2 border-gray-300 rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap">
            <i className="ri-printer-line"></i>
            <span>Print Terms</span>
          </button>
          <button className="flex items-center gap-2 px-6 py-3 bg-white text-gray-700 border-2 border-gray-300 rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap">
            <i className="ri-mail-line"></i>
            <span>Email Copy</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default TermsPage;
